import { useCallback, useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { PiFileBold, PiUploadSimpleBold, PiXBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

export type FileDropzoneProps = {
    accept?: string;
    /** Texto amigável dos formatos (ex.: "PDF ou XML"). Se omitido, não lista MIME crus. */
    acceptLabel?: string;
    multiple?: boolean;
    disabled?: boolean;
    loading?: boolean;
    /** Limite em MB (validação client-side). */
    maxSizeMb?: number;
    label?: string;
    hint?: string;
    className?: string;
    /** Classes extras na área clicável/arrastável. */
    zoneClassName?: string;
    /** Se true, mostra o nome do arquivo escolhido até limpar / novo envio. */
    showSelected?: boolean;
    onFiles: (files: File[]) => void;
    onError?: (message: string) => void;
    children?: ReactNode;
};

function acceptMatches(file: File, accept: string): boolean {
    const tokens = accept
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    if (tokens.length === 0) return true;

    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();

    return tokens.some((token) => {
        if (token.startsWith('.')) {
            return name.endsWith(token);
        }
        if (token.endsWith('/*')) {
            return type.startsWith(token.slice(0, -1));
        }
        return type === token || name.endsWith(`.${token}`);
    });
}

export default function FileDropzone({
    accept,
    acceptLabel,
    multiple = false,
    disabled = false,
    loading = false,
    maxSizeMb,
    label = 'Arraste o arquivo aqui',
    hint = 'ou clique para selecionar',
    className,
    zoneClassName,
    showSelected = true,
    onFiles,
    onError,
    children,
}: FileDropzoneProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = useId();
    const [dragging, setDragging] = useState(false);
    const [selected, setSelected] = useState<File[]>([]);

    const blocked = disabled || loading;

    const handleFiles = useCallback(
        (list: FileList | File[] | null) => {
            if (!list || blocked) return;
            const files = Array.from(list);
            if (files.length === 0) return;

            const picked = multiple ? files : files.slice(0, 1);

            if (accept) {
                const invalid = picked.find((f) => !acceptMatches(f, accept));
                if (invalid) {
                    onError?.(`Arquivo não suportado: ${invalid.name}`);
                    return;
                }
            }

            if (maxSizeMb != null) {
                const maxBytes = maxSizeMb * 1024 * 1024;
                const tooBig = picked.find((f) => f.size > maxBytes);
                if (tooBig) {
                    onError?.(`Arquivo acima de ${maxSizeMb} MB: ${tooBig.name}`);
                    return;
                }
            }

            setSelected(picked);
            onFiles(picked);
            if (inputRef.current) inputRef.current.value = '';
        },
        [accept, blocked, maxSizeMb, multiple, onError, onFiles],
    );

    function onDragOver(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (!blocked) setDragging(true);
    }

    function onDragLeave(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    }

    function onDrop(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    }

    return (
        <div className={cn('w-full', className)}>
            <label
                htmlFor={inputId}
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={cn(
                    'flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition',
                    dragging
                        ? 'border-primary bg-primary/5 scale-[1.01] dark:bg-primary/10'
                        : 'border-gray-300 bg-gray-50/90 hover:border-primary/50 hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/60 dark:hover:border-primary/40 dark:hover:bg-zinc-900',
                    blocked && 'cursor-not-allowed opacity-60',
                    zoneClassName,
                )}
            >
                <input
                    id={inputId}
                    ref={inputRef}
                    type="file"
                    className="sr-only"
                    accept={accept}
                    multiple={multiple}
                    disabled={blocked}
                    onChange={(e) => handleFiles(e.target.files)}
                />

                {children ?? (
                    <>
                        <span className={cn(
                            'mb-4 flex size-14 items-center justify-center rounded-full',
                            dragging
                                ? 'bg-primary text-white'
                                : 'bg-white text-gray-600 shadow-sm ring-1 ring-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-700',
                        )}>
                            <PiUploadSimpleBold className="size-7" />
                        </span>
                        <p className="text-base font-semibold text-gray-900 dark:text-zinc-100">
                            {loading ? 'Enviando…' : label}
                        </p>
                        {!loading && (
                            <p className="mt-1.5 text-sm text-gray-500 dark:text-zinc-400">{hint}</p>
                        )}
                        {(acceptLabel || maxSizeMb != null) && !loading && (
                            <p className="mt-3 text-xs font-medium text-gray-400 dark:text-zinc-500">
                                {[acceptLabel, maxSizeMb != null ? `até ${maxSizeMb} MB` : null]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </p>
                        )}
                    </>
                )}
            </label>

            {showSelected && selected.length > 0 && !loading && (
                <ul className="mt-2 space-y-1">
                    {selected.map((file) => (
                        <li
                            key={`${file.name}-${file.size}`}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        >
                            <PiFileBold className="size-3.5 shrink-0 text-gray-400" />
                            <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                            <span className="shrink-0 text-gray-400">
                                {(file.size / 1024).toFixed(0)} KB
                            </span>
                            <button
                                type="button"
                                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-zinc-800"
                                onClick={() => setSelected([])}
                                aria-label="Limpar seleção"
                            >
                                <PiXBold className="size-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
